import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import ModuleLibrary from './pages/ModuleLibrary';
import ModuleDetail from './pages/ModuleDetail';
import ModuleViewer from './pages/ModuleViewer';
import ProgressTracking from './pages/ProgressTracking';
import AdminPanel from './pages/AdminPanel';
import AdminDashboard from './pages/AdminDashboard';
import ModuleAssignments from './pages/ModuleAssignments';
import WorkloadDashboard from './pages/WorkloadDashboard';
import WorkloadSettings from './pages/WorkloadSettings';
import DocumentLibrary from './pages/DocumentLibrary';
import StudyGroups from './pages/StudyGroups';
import MentorDashboard from './pages/MentorDashboard';
import Help from './pages/Help';
import AssignmentHistory from './pages/AssignmentHistory';
import SetPassword from './pages/SetPassword';

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Require JWT authentication - redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/" element={
            <ProtectedRoute>
              <Layout><Dashboard /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Layout><Dashboard /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/modules" element={
          <ProtectedRoute>
            <Layout><ModuleLibrary /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/modules/:moduleId" element={
          <ProtectedRoute>
            <Layout><ModuleDetail /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/module/:moduleCode" element={
          <ProtectedRoute>
            <Layout><ModuleViewer /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/progress" element={
          <ProtectedRoute>
            <Layout><ProgressTracking /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute>
            <Layout><AdminPanel /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/manager-dashboard" element={
          <ProtectedRoute>
            <Layout><AdminDashboard /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/assignments" element={
          <ProtectedRoute>
            <Layout><ModuleAssignments /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/documents" element={
          <ProtectedRoute>
            <Layout><DocumentLibrary /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/study-groups" element={
          <ProtectedRoute>
            <Layout><StudyGroups /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/mentorships" element={
          <ProtectedRoute>
            <Layout><MentorDashboard /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/workload" element={
          <ProtectedRoute>
            <Layout><WorkloadDashboard /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/workload-settings" element={
          <ProtectedRoute>
            <Layout><WorkloadSettings /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/help" element={
          <ProtectedRoute>
            <Layout><Help /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/assignment-history" element={
          <ProtectedRoute>
            <Layout><AssignmentHistory /></Layout>
          </ProtectedRoute>
        } />
        {/* Catch-all route - redirect to login if not authenticated */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Router>
    </AuthProvider>
  );
}

export default App;

